import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import DeleteIcon from '@atlaskit/icon/core/delete';
import {
  attachClosestEdge,
  type Edge,
  extractClosestEdge
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DragHandleButton } from '@atlaskit/pragmatic-drag-and-drop-react-accessibility/drag-handle-button';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl-next';

import ListItemPositionDropdown from './ListItemPositionDropdown.tsx';
import { messages } from './messages.ts';
import { useListContext } from './useListContext.ts';

export const itemKey = Symbol('item');

export interface ItemValue {
  id: string;
  name: string;
  label: string;
  contentTitle?: string;
}

const listStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  height: 'auto',
  boxSizing: 'border-box',
  position: 'relative',
  borderWidth: '0',
  borderBottomWidth: 'border.width',
  borderBottomColor: 'transparent',
  borderStyle: 'solid',
  justifyContent: 'center',
  paddingInlineStart: 'space.075'
});

const buttonContainer = xcss({
  paddingInline: 'space.050'
});

const compactDeleteButtonContainer = xcss({
  display: 'flex',
  justifyContent: 'center',
  width: 'space.400'
});

const dropIndicatorContainerStyles = xcss({
  position: 'absolute',
  insetBlock: 'space.0',
  insetInline: 'space.100',
  pointerEvents: 'none'
});

const listItemOnHover = xcss({
  ':hover': {
    backgroundColor: 'color.background.neutral'
  }
});

const listActiveNeutralBackground = xcss({
  backgroundColor: 'color.background.neutral'
});

const borderIsEditing = xcss({
  border: 'border.width.200',
  borderColor: 'color.border.focused'
});

const borderIsEditingIsError = xcss({
  border: 'border.width.200',
  borderColor: 'color.border.danger'
});

const borderIsNotEditing = xcss({
  border: 'space.025'
});

const listItemPreviewStyles = xcss({
  paddingBlock: 'space.050',
  paddingInline: 'space.100',
  borderRadius: 'radius.small',
  backgroundColor: 'elevation.surface.overlay',
  maxWidth: '360px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
});

const listItemDisabledStyles = xcss({ opacity: 0.4 });

type ListState<T> = {
  items: T[];
  lastCardMoved: {
    item: T;
    dragHandleRef?: React.RefObject<HTMLButtonElement>;
  } | null;
};

type ItemPosition = 'first' | 'last' | 'middle' | 'only';

export interface ListItemProps<T, U> {
  item: T;
  itemId: string;
  index: number;
  position: ItemPosition;
  renderField: (data: {
    i: number;
    value: T;
    autoFocus: boolean;
    onChange: (value: T, delta: U) => void;
    isReadonly: boolean;
    isDisabled: boolean;
    duplicateId?: string | undefined;
  }) => React.ReactNode;
  onReorder?: (from: number, to: number) => void;
  onValueChange?: (i: number, value: T, delta: U) => void;
  isDisabled?: boolean;
  isReadonly?: boolean;
  autoFocus?: number | 'addButton';
  noDelete?: boolean;
  deleteTooltip?: string;
  onDelete?: (index: number) => void;
  activeId?: string;
  editingId?: string;
  errorId?: string;
  setListState: React.Dispatch<React.SetStateAction<ListState<T>>>;
  isHovering?: boolean;
  renderDragImage?: (value: T) => React.ReactNode;
  showHoverBackground?: boolean;
  duplicateId?: string | undefined;
  getItemLabel?: (value: T) => string;
  hasInteractedWithList?: boolean;
  onListInteraction?: () => void;
  compactDeleteButton?: boolean;
}

type DraggableState =
  | { type: 'idle' }
  | { type: 'preview'; container: HTMLElement }
  | { type: 'dragging' };

const idleState: DraggableState = { type: 'idle' };
const draggingState: DraggableState = { type: 'dragging' };

type ItemData<T> = {
  [itemKey]: true;
  item: T;
  index: number;
  instanceId: symbol;
};

function getItemData<T>({
  item,
  index,
  instanceId
}: {
  item: T;
  index: number;
  instanceId: symbol;
}): ItemData<T> {
  return {
    [itemKey]: true,
    item,
    index,
    instanceId
  };
}

function isItemData<T>(data: Record<string | symbol, unknown>): data is ItemData<T> {
  return data[itemKey] === true;
}

export default function ListItem<T, U>({
  item,
  itemId,
  index,
  position,
  renderField,
  onReorder,
  onValueChange,
  isDisabled,
  isReadonly,
  autoFocus,
  noDelete,
  deleteTooltip,
  onDelete,
  activeId,
  errorId,
  editingId,
  setListState,
  isHovering,
  renderDragImage,
  showHoverBackground,
  duplicateId,
  getItemLabel,
  hasInteractedWithList,
  onListInteraction,
  compactDeleteButton = false
}: ListItemProps<T, U>): React.ReactElement {
  const { formatMessage } = useIntl();
  const { registerItem, instanceId } = useListContext();

  const ref = useRef<HTMLDivElement>(null);
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);

  const dragHandleRef = useRef<HTMLButtonElement>(null);

  const [draggableState, setDraggableState] = useState<DraggableState>(idleState);

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    if (!dragHandleRef.current) {
      return;
    }

    const element = ref.current;

    const data = getItemData({ item, index, instanceId });

    return combine(
      registerItem({ itemId, element }),
      draggable({
        element,
        dragHandle: dragHandleRef.current,
        getInitialData: () => data,
        onGenerateDragPreview({ nativeSetDragImage }) {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: pointerOutsideOfPreview({
              x: token('space.200', '16px'),
              y: token('space.100', '8px')
            }),
            render({ container }) {
              setDraggableState({ type: 'preview', container });

              return () => setDraggableState(draggingState);
            }
          });
        },
        onDragStart() {
          setDraggableState(draggingState);
        },
        onDrop() {
          setDraggableState(idleState);
        }
      }),
      dropTargetForElements({
        element,
        canDrop({ source }) {
          return isItemData(source.data) && source.data.instanceId === instanceId;
        },
        getData({ input }) {
          return attachClosestEdge(data, {
            element,
            input,
            allowedEdges: ['top', 'bottom']
          });
        },
        onDrag({ self, source }) {
          const isSource = source.element === element;
          if (isSource) {
            setClosestEdge(null);
            return;
          }

          const closestEdge = extractClosestEdge(self.data);

          const sourceIndex = source.data.index;
          if (typeof sourceIndex !== 'number') {
            return;
          }

          const isItemBeforeSource = index === sourceIndex - 1;
          const isItemAfterSource = index === sourceIndex + 1;

          const isDropIndicatorHidden =
            (isItemBeforeSource && closestEdge === 'bottom') ||
            (isItemAfterSource && closestEdge === 'top');

          if (isDropIndicatorHidden) {
            setClosestEdge(null);
            return;
          }

          setClosestEdge(closestEdge);
        },
        onDragLeave() {
          setClosestEdge(null);
        },
        onDrop() {
          setClosestEdge(null);
        }
      })
    );
  }, [instanceId, item, index, registerItem, itemId]);

  const itemName = (item as ItemValue).name;
  const itemLabel = getItemLabel?.(item) ?? (item as ItemValue).label;
  const contentTitle = (item as ItemValue).contentTitle;
  const isActive = itemId === activeId;
  const isEditing = itemId === editingId;
  const hasError = itemId === errorId;

  return (
    <Fragment>
      <li style={{ marginTop: 0 }}>
        <Box
          ref={ref}
          onMouseEnter={onListInteraction}
          xcss={[
            listStyles,
            isActive && !hasInteractedWithList && listActiveNeutralBackground,
            isEditing && borderIsEditing,
            isEditing && hasError && borderIsEditingIsError,
            !isEditing && borderIsNotEditing,
            showHoverBackground && listItemOnHover,
            draggableState.type === 'dragging' && listItemDisabledStyles
          ]}
        >
          {!isReadonly && (
            <DropdownMenu
              testId="drag-handle-dropdown-menu"
              spacing="compact"
              trigger={({ triggerRef, ...triggerProps }) => (
                <Box xcss={buttonContainer}>
                  <DragHandleButton
                    ref={mergeRefs([dragHandleRef, triggerRef])}
                    {...triggerProps}
                    label={formatMessage(messages['draggable-list.drag-handle-label'])}
                    data-testid="drag-handle"
                    tabIndex={0}
                    appearance="subtle"
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      opacity: isHovering ? '1' : '0',
                      color: token('color.text.subtle')
                    }}
                  />
                </Box>
              )}
            >
              <DropdownItemGroup>
                <ListItemPositionDropdown
                  position={position}
                  index={index}
                  item={item}
                  onReorder={onReorder}
                  setListState={setListState}
                />
              </DropdownItemGroup>
            </DropdownMenu>
          )}
          {renderField({
            i: index,
            value: item,
            onChange: (val, delta) => {
              onValueChange?.(index, val, delta);
            },
            isDisabled: isDisabled || false,
            isReadonly: isReadonly || false,
            autoFocus: autoFocus === index,
            duplicateId: duplicateId || undefined
          })}
          {closestEdge && (
            <Box xcss={dropIndicatorContainerStyles}>
              <DropIndicator edge={closestEdge} gap="1px" />
            </Box>
          )}
          {!noDelete && !isReadonly ? (
            <Box
              xcss={[
                buttonContainer,
                compactDeleteButton && compactDeleteButtonContainer,
                !isDisabled && duplicateId
                  ? xcss({ marginBottom: 'space.500' })
                  : undefined
              ]}
            >
              <Tooltip content={deleteTooltip}>
                <Button
                  testId="list-item-delete"
                  iconBefore={
                    <DeleteIcon
                      label=""
                      spacing="compact"
                      color={token('color.icon.subtle')}
                    />
                  }
                  onClick={() => {
                    onDelete?.(index);
                  }}
                  appearance="subtle"
                  spacing={compactDeleteButton ? 'none' : 'compact'}
                  isDisabled={isDisabled}
                  style={{
                    opacity: isHovering ? '1' : '0'
                  }}
                  aria-label={formatMessage(
                    messages['draggable-list.delete-item-label'],
                    {
                      name: itemLabel
                    }
                  )}
                />
              </Tooltip>
            </Box>
          ) : (
            <div />
          )}
        </Box>
      </li>
      {draggableState.type === 'preview' &&
        ReactDOM.createPortal(
          renderDragImage
            ? renderDragImage(item)
            : (itemName || itemLabel || contentTitle) && (
                <Box xcss={listItemPreviewStyles}>
                  {itemName || itemLabel || contentTitle}
                </Box>
              ),
          draggableState.container
        )}
    </Fragment>
  );
}
